"use client";
import { useEffect, useState } from "react";
// The one shared definition of "an interaction" — see that module for why
// `scroll` is excluded and why the GTM loader must use the same list.
import { INTERACTION_EVENTS } from "@/lib/interaction-events";

/**
 * Returns `false` until the first real user interaction, then `true`.
 *
 * Used to defer third-party scripts (AdSense, GTM, Funding Choices) off the
 * initial page load. Bounce visitors and synthetic loads (Lighthouse, SEO
 * crawlers, link previewers) never interact, so they never load the ad
 * stack and never get third-party cookies set — which keeps the Lighthouse
 * "third-party cookies" / Best Practices audits clean and shortens the
 * critical path. Any engaged visitor taps or scrolls within the first
 * moment and gets the full stack.
 */
export function useFirstInteraction(): boolean {
  const [interacted, setInteracted] = useState(false);

  useEffect(() => {
    if (interacted) return;
    const trigger = () => setInteracted(true);
    for (const ev of INTERACTION_EVENTS) {
      window.addEventListener(ev, trigger, { once: true, passive: true });
    }
    return () => {
      for (const ev of INTERACTION_EVENTS) {
        window.removeEventListener(ev, trigger);
      }
    };
  }, [interacted]);

  return interacted;
}
